import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
import { transactionStore, ManualSettlement } from '../utils/transactionStore';
const CheckerManualSettlementTable: React.FC = () => {
  const [settlements, setSettlements] = useState<ManualSettlement[]>([]);
  const [selectedSettlement, setSelectedSettlement] = useState<ManualSettlement | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const refreshSettlements = () => {
    setSettlements(transactionStore.getPendingManualSettlements());
  };
  useEffect(() => {
    refreshSettlements();
    const unsubscribe = transactionStore.subscribe(refreshSettlements);
    return unsubscribe;
  }, []);
  const handleAction = (settlement: ManualSettlement, action: 'approve' | 'reject') => {
    setSelectedSettlement(settlement);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedSettlement || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') {
        transactionStore.approveManualSettlement(selectedSettlement.id, reason);
      } else {
        transactionStore.rejectManualSettlement(selectedSettlement.id, reason);
      }
      showToast(`Manual settlement ${actionType === 'approve' ? 'approved' : 'rejected'} for ${selectedSettlement.transactionId}`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelectedSettlement(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (settlement: ManualSettlement) => {
    setSelectedSettlement(settlement);
    setDetailsDrawerOpen(true);
  };
  const rowActions: RowAction<ManualSettlement>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<ManualSettlement>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-670" data-mp="3tevlpdgsuy88">
      
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-671" data-mp="1zwodvj8x09bh">
      
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-672" data-mp="2rox4w7lyk9go">
          {row.mtoName}
        </Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: 'rgba(139, 92, 246, 0.1)',
      color: '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-673" data-mp="2inp85tz3bgop" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-674" data-mp="1c6tzuycv0epn">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-675" data-mp="1mpie9h293r6a">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-676" data-mp="1dpluntupxii3">
      
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-677" data-mp="25ep19ga2v4xw">
          {row.requestedBy}
        </Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-678" data-mp="1whxv2b1ta9pl">
      
          {new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}
        </Typography>
  }];
  return <>
      <DataTable title="Pending Manual Settlements" data={settlements} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending manual settlement requests" initialVisibleColumns={['transactionId', 'mtoName', 'payoutType', 'amountFCY', 'beneficiaryName', 'makerReason', 'requestedBy', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-679" data-mp="2wotadj70pp4o" />
      

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-680" data-mp="3kbrnc6vxiexu">
        
        <DialogTitle data-id="element-681" data-mp="3tekb405wk8ix">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-682" data-mp="3tdr3kwz4dezq">
            
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-683" data-mp="1yzzvw30cp56x" />
            
            <Typography variant="h6" fontWeight={600} data-id="element-684" data-mp="2xef73fls3wwo">
              {actionType === 'approve' ? 'Approve' : 'Reject'} Manual
              Settlement
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-685" data-mp="3la1ft9tsmytt">
          <Box sx={{
          mt: 1
        }} data-id="element-686" data-mp="3l99km043z342">
            
            {selectedSettlement && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-687" data-mp="3gaoifv5aq9cz">
                
                  Transaction ID:{' '}
                  <strong data-id="element-688" data-mp="3q340stki8zye">{selectedSettlement.transactionId}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-689" data-mp="7ssd2zljtass">
                
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-690" data-mp="mg0wgnp0gmfu">
                  
                    Maker's Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-691" data-mp="hi7njmwbpebv">
                  
                    {selectedSettlement.makerReason}
                  </Typography>
                </Paper>

                {selectedSettlement.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-692" data-mp="3pzlnowbfkoc0">
                
                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-693" data-mp="3lwpdntzyi0jo">
                  
                      Previous Rejection Reason
                    </Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-694" data-mp="bj4yuheev6h">
                  
                      {selectedSettlement.checkerComment}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-695" data-mp="3ige313xxaw3m">
                  
                      Maker's Resubmit Reason
                    </Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-696" data-mp="38qkq9zm1oaxs">
                  
                      {selectedSettlement.resubmitReason}
                    </Typography>
                  </Paper>}
              </>}

            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this settlement...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-697" data-mp="2usw6yw7mlbuu" />
            
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-698" data-mp="rg6r5knak58h">
          
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-699" data-mp="10iobtunasptd">
            
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-701" data-mp="3jo2op7itep4m" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-702" data-mp="3jpm6j2k4d88i" />} data-id="element-700" data-mp="15azayva7s7kv">
            
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedSettlement && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedSettlement(null);
    }} transaction={{
      id: selectedSettlement.transactionId,
      mtoName: selectedSettlement.mtoName,
      mtoRef: `MTO-${selectedSettlement.transactionId}`,
      bankRef: `BNK-${selectedSettlement.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedSettlement.payoutType,
      amountFCY: selectedSettlement.amountFCY,
      currencyFCY: selectedSettlement.currencyFCY,
      amountPKR: (parseFloat(selectedSettlement.amountFCY) * 280).toString(),
      corridor: 'PK-US',
      purposeCode: 'FAM',
      purposeText: 'Family Support',
      currentState: selectedSettlement.currentState as any,
      lifecycleStatus: 'FAILED' as any,
      createdAt: selectedSettlement.requestedAt,
      updatedAt: selectedSettlement.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: selectedSettlement.beneficiaryName,
      beneficiaryIBAN: selectedSettlement.beneficiaryIBAN,
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-703" data-mp="3002aksp4ruhw" />}
    </>;
};
export default CheckerManualSettlementTable;